import { Alert, Stack, Title } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import type { ColDef } from "ag-grid-community";
import { useTRPC } from "../../../trpc";
import { UserCellRenderer } from "../../../utils/columns";
import { useIntl } from "react-intl";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { RouterOutputs } from "@project/api";
import { AgGridTable } from "../../../components/AgGridTable.js";

type Audit = RouterOutputs["getAuditLogs"][number];

type AuditWithAPIKey = Audit & { apiKey: { name: string } };
type AuditWithUser = Audit & { user: { id: number; name: string; avatar: string | null } };

export const AuditLog = () => {
  const intl = useIntl();
  const trpc = useTRPC();
  const { data = [] } = useQuery(trpc.getAuditLogs.queryOptions());

  const columnsAPIKey: ColDef<AuditWithAPIKey>[] = useMemo(() => {
    return [
      {
        field: "id",
        headerName: intl.formatMessage({ defaultMessage: "ID", id: "column.id" }),
      },
      {
        field: "apiKey.name",
        headerName: intl.formatMessage({ defaultMessage: "APIKey", id: "column.apiKey" }),
      },
      {
        field: "action",
        headerName: intl.formatMessage({ defaultMessage: "Action", id: "column.action" }),
      },
      {
        field: "createdAt",
        headerName: intl.formatMessage({ defaultMessage: "Created at", id: "column.createdAt" }),
        sortable: true,
        cellDataType: "dateTimeString",
      },
    ];
  }, [intl]);

  const columnsUser: ColDef<AuditWithUser>[] = useMemo(() => {
    return [
      {
        field: "id",
        headerName: intl.formatMessage({ defaultMessage: "ID", id: "column.id" }),
      },
      {
        field: "user",
        headerName: intl.formatMessage({ defaultMessage: "User", id: "column.user" }),
        cellRenderer: UserCellRenderer,
      },
      {
        field: "action",
        headerName: intl.formatMessage({ defaultMessage: "Action", id: "column.action" }),
      },
      {
        field: "createdAt",
        headerName: intl.formatMessage({ defaultMessage: "Created at", id: "column.createdAt" }),
        sortable: true,
        cellDataType: "dateTimeString",
      },
    ];
  }, [intl]);

  const dataUsers = useMemo(() => data.filter(d => d.user) as AuditWithUser[], [data]);
  const dataAPIKeys = useMemo(() => data.filter(d => d.apiKey) as AuditWithAPIKey[], [data]);

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Manage account", id: "navigation.manageAccount" })}
        current={intl.formatMessage({ defaultMessage: "Audit log", id: "navigation.auditLog" })}
        description={intl.formatMessage({ defaultMessage: "See the history of changes made within this account.", id: "text.AuditLogDescription" })}
      />
      {dataUsers.length === 0 && dataAPIKeys.length === 0 && (
        <Alert
          variant="light"
          title={intl.formatMessage({ defaultMessage: "No actions", id: "title.noActions" })}
        />
      )}
      <Stack mr="1rem">
        {dataUsers.length > 0 && (
          <>
            <Title size="md">{intl.formatMessage({ defaultMessage: "Users actions", id: "title.usersActions" })}</Title>
            <AgGridTable
              rowData={dataUsers}
              columnDefs={columnsUser}
              domLayout="autoHeight"
              pagination={true}
              paginationPageSize={10}
              autoSizeStrategy={{
                type: "fitGridWidth",
                defaultMinWidth: 100,
              }}
              suppressHorizontalScroll={false}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
              }}
            />
          </>
        )}
        {dataAPIKeys.length > 0 && (
          <>
            <Title size="md">{intl.formatMessage({ defaultMessage: "API keys actions", id: "title.apiKeysActions" })}</Title>
            <AgGridTable
              rowData={dataAPIKeys}
              columnDefs={columnsAPIKey}
              domLayout="autoHeight"
              suppressHorizontalScroll={false}
              pagination={true}
              paginationPageSize={10}
              autoSizeStrategy={{
                type: "fitGridWidth",
                defaultMinWidth: 100,
              }}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
              }}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};
